import { useMemo, useRef, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Chip, Divider, Paper, Tooltip, Typography } from '@mui/material'

import {
  selectRows, selectDisplayFrom, selectDisplayTo,
  selectSelectedType, selectSelectedDayIdx,
  selectHighlightGapDays, selectVendorFilter, selectVolumeThreshold,
  selectMedianWindow,
  setSelectedDayIdx, setSelectedDrillType,
  setVendorFilter, toggleHighlightGapDays,
} from '../../reducers/dataIntegrityReducer'
import {
  buildLookup, getUniqueDates, getDisplayDates, getUniqueTypes,
  getUniqueSides, cellStatus, colorForStatus, computeMetrics,
} from '../../utils/dataIntegrityUtils'

const LEGEND = [
  { color: '#2e7d32', label: 'all vendors with data' },
  { color: '#f9a825', label: 'volume below threshold' },
  { color: '#ef6c00', label: 'some vendors missing'    },
  { color: '#c62828', label: 'no data'                  },
]

const TYPE_ORDER  = ['Site', 'Cell']
const LABEL_WIDTH = 68
const GAP         = 2
const CELL_MIN    = 10   // below this → scroll with fixed size
const CELL_MAX    = 20   // max cell size — prevents giant cells on short periods
const CELL_FIXED  = 16   // fixed cell size when scrolling

function HeatmapSection({ type, sides, dates, allDates, medianWindow, lookup, cellSize, selectedDayIdx, onSelect, highlightGapDays, gapDayDates, volumeThreshold }) {
  return (
    <Box>
      <Typography variant="caption" fontWeight={600} sx={{
        display: 'block', color: 'text.secondary',
        letterSpacing: '0.07em', textTransform: 'uppercase',
        mb: 0.5, pl: `${LABEL_WIDTH}px`,
      }}>
        {type}
      </Typography>

      {sides.map(side => (
        <Box key={side} sx={{ display: 'flex', alignItems: 'center', mb: `${GAP}px` }}>
          <Typography variant="caption" color="text.secondary" sx={{
            width: LABEL_WIDTH, flexShrink: 0,
            textAlign: 'right', pr: 1, fontSize: 10,
          }}>
            {side}
          </Typography>
          <Box sx={{ display: 'flex', gap: `${GAP}px` }}>
            {dates.map((date, i) => {
              const { status, missing, lowVolume } = cellStatus(lookup, type, side, date, allDates, volumeThreshold, medianWindow)
              const isSelected = selectedDayIdx === i
              const isGapDay   = highlightGapDays && gapDayDates.includes(date)

              const tooltipText = status === 'all'
                ? `${type} · ${side} · ${date} · all vendors with data`
                : status === 'low'
                ? `${type} · ${side} · ${date} · low volume: ${lowVolume.join(', ')}`
                : status === 'partial'
                ? `${type} · ${side} · ${date} · missing: ${missing.join(', ')}`
                : `${type} · ${side} · ${date} · no data`

              return (
                <Tooltip key={date} title={tooltipText} arrow placement="top">
                  <Box
                    onClick={() => onSelect(i, type)}
                    sx={{
                      width: cellSize, height: cellSize,
                      borderRadius: '3px',
                      bgcolor: colorForStatus(status),
                      cursor: 'pointer', flexShrink: 0,
                      outline: isSelected
                        ? '2px solid #1565c0'
                        : isGapDay && status !== 'all'
                        ? '2px solid #e65100'
                        : 'none',
                      outlineOffset: '1px',
                      transition: 'transform .12s',
                      '&:hover': { transform: 'scale(1.3)', zIndex: 2, position: 'relative' },
                    }}
                  />
                </Tooltip>
              )
            })}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export default function DataIntegrityHeatmap() {
  const dispatch         = useDispatch()
  const rows             = useSelector(selectRows)
  const selectedType     = useSelector(selectSelectedType)
  const selectedDayIdx   = useSelector(selectSelectedDayIdx)
  const highlightGapDays = useSelector(selectHighlightGapDays)
  const vendorFilter     = useSelector(selectVendorFilter)
  const volumeThreshold  = useSelector(selectVolumeThreshold)
  const medianWindow     = useSelector(selectMedianWindow)
  const displayFrom      = useSelector(selectDisplayFrom)
  const displayTo        = useSelector(selectDisplayTo)

  const paperRef  = useRef(null)
  const scrollRef = useRef(null)
  const [containerW, setContainerW] = useState(0)

  // Measure available width (Paper minus padding)
  useEffect(() => {
    if (!paperRef.current) return
    const ro = new ResizeObserver(() => {
      if (paperRef.current) {
        const style  = window.getComputedStyle(paperRef.current)
        const padL   = parseFloat(style.paddingLeft)  || 16
        const padR   = parseFloat(style.paddingRight) || 16
        setContainerW(paperRef.current.clientWidth - padL - padR)
      }
    })
    ro.observe(paperRef.current)
    return () => ro.disconnect()
  }, [])

  // Scroll selected cell into view
  useEffect(() => {
    if (selectedDayIdx == null || !scrollRef.current) return
    const cellTotal  = CELL_FIXED + GAP
    const scrollLeft = selectedDayIdx * cellTotal - 100
    scrollRef.current.scrollLeft = Math.max(0, scrollLeft)
  }, [selectedDayIdx])

  const { lookup, dates, allDates, types, sides, gapDayDates, cellSize, needsScroll, labelInterval } = useMemo(() => {
    const lookup       = buildLookup(rows)
    const allDates     = getUniqueDates(rows)
    const dates        = getDisplayDates(rows, displayFrom, displayTo)
    const allTypes     = getUniqueTypes(rows)
    const sides        = getUniqueSides(rows)
    const metrics      = computeMetrics(rows, volumeThreshold, displayFrom, displayTo, medianWindow)

    const types = TYPE_ORDER
      .filter(t => selectedType === 'both' ? true : t === selectedType)
      .filter(t => allTypes.includes(t))

    const n = Math.max(dates.length, 1)

    // Calculate cell size to fill available width
    const available  = containerW > 0 ? containerW - LABEL_WIDTH : 600
    const computed   = Math.floor((available - (n - 1) * GAP) / n)
    const needsScroll = computed < CELL_MIN
    const cellSize    = needsScroll ? CELL_FIXED : Math.min(computed, CELL_MAX)

    // Label interval: show every date when cells are wide enough, else skip
    const labelInterval = cellSize >= 14 ? 1
      : cellSize >= 10  ? 2
      : dates.length <= 60 ? 2 : 4

    return { lookup, dates, allDates, types, sides, gapDayDates: metrics.gapDayDates ?? [], cellSize, needsScroll, labelInterval }
  }, [rows, selectedType, volumeThreshold, medianWindow, containerW, displayFrom, displayTo])

  const handleSelect = (dayIdx, type) => {
    dispatch(setSelectedDayIdx(dayIdx))
    dispatch(setSelectedDrillType(type))
  }

  if (!rows.length) return null

  const totalWidth = dates.length * (cellSize + GAP) - GAP

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 1.5 }} ref={paperRef}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="body2" fontWeight={500}>
          Data presence per day — click a cell to see the detail
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {highlightGapDays && (
            <Chip size="small" label={`${gapDayDates.length} days with gaps`}
              color="error" variant="outlined"
              onDelete={() => dispatch(toggleHighlightGapDays())}
              sx={{ fontSize: 11 }} />
          )}
          {vendorFilter && (
            <Chip size="small" label={`Vendor: ${vendorFilter}`}
              color="warning" variant="outlined"
              onDelete={() => dispatch(setVendorFilter(null))}
              sx={{ fontSize: 11 }} />
          )}
        </Box>
      </Box>

      {/* Scroll wrapper — only scrolls when needed */}
      <Box
        ref={scrollRef}
        sx={{ overflowX: needsScroll ? 'auto' : 'hidden', overflowY: 'visible', pb: needsScroll ? 1 : 0 }}
      >
        <Box sx={{ width: LABEL_WIDTH + totalWidth, minWidth: needsScroll ? 'max-content' : '100%' }}>

          {/* Date axis */}
          <Box sx={{ display: 'flex', gap: `${GAP}px`, ml: `${LABEL_WIDTH}px`, mb: 0.5, alignItems: 'flex-end', height: 72 }}>
            {dates.map((d, i) => (
              <Box key={d} sx={{ width: cellSize, flexShrink: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', height: '100%' }}>
                {i % labelInterval === 0 && (
                  <Typography variant="caption" sx={{
                    fontSize: 9, color: 'text.disabled',
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    whiteSpace: 'nowrap', lineHeight: 1,
                  }}>
                    {d}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>

          {/* Rows by type */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {types.map((type, ti) => (
              <Box key={type}>
                {ti > 0 && <Divider sx={{ mb: 1 }} />}
                <HeatmapSection
                  type={type} sides={sides} dates={dates} allDates={allDates} medianWindow={medianWindow}
                  lookup={lookup} cellSize={cellSize}
                  selectedDayIdx={selectedDayIdx} onSelect={handleSelect}
                  highlightGapDays={highlightGapDays}
                  gapDayDates={gapDayDates}
                  volumeThreshold={volumeThreshold}
                />
              </Box>
            ))}
          </Box>

        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.disabled">Status:</Typography>
        {LEGEND.map(l => (
          <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: l.color, flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary">{l.label}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  )
}